/**
 * Pure helpers for the Subsequent Purchases table. The cube returns PAIR rows
 * (one per next-order item) and a single BASE row (the denominators for the
 * selected original item); these join them into the displayed rows and apply
 * the table's client-side sort (TableVisualization runs with manualSorting).
 */

export type RawPair = {
  itemKey: string
  title: string
  handle: string | null
  subsequentOrders: number
  subsequentQuantity: number
  subsequentRevenue: number
  /**
   * Calendar days to the next order, summed over the pair's original orders;
   * null when the table predates the timing columns.
   */
  sumDaysToNext: number | null
}

/** Upper edges (days) of the cumulative order-gap buckets the base row carries. */
export const GAP_BUCKET_DAYS = [30, 60, 90, 180, 365] as const

export type GapBucketDays = (typeof GAP_BUCKET_DAYS)[number]

export type GapTiming = {
  /** Calendar days to the next order, summed over ordersWithNext. */
  sumDaysToNext: number
  /** Of ordersWithNext, how many saw the next order within N days (cumulative). */
  nextWithin: Record<GapBucketDays, number>
}

export type Denominators = {
  /** Distinct original orders containing the selected item. */
  originalOrders: number
  /** Those whose customer placed a later order (any content). */
  ordersWithNext: number
  /** Order-gap timing from the base row; null when the table predates those columns. */
  timing: GapTiming | null
}

export type SubsequentRow = RawPair & {
  id: string
  /** subsequentOrders / ordersWithNext — "of the customers who ordered again". */
  nextOrderShare: number | null
  /** subsequentOrders / originalOrders — "of everyone who bought the item". */
  allOrdersShare: number | null
  /** sumDaysToNext / subsequentOrders — mean gap for customers who went on to this item. */
  avgDaysToNext: number | null
}

export type SortSpec = { id: string; desc: boolean }

// Sort by the headline conversion share. Within one selection both shares rank
// identically (same numerator, fixed denominators), so this only decides which
// column carries the sort indicator.
export const DEFAULT_SORTING: SortSpec[] = [
  { id: "allOrdersShare", desc: true },
]

export const EMPTY_DENOMINATORS: Denominators = {
  originalOrders: 0,
  ordersWithNext: 0,
  timing: null,
}

export const safeShare = (numerator: number, denominator: number) =>
  denominator > 0 ? numerator / denominator : null

export const repeatRate = (denominators: Denominators) =>
  safeShare(denominators.ordersWithNext, denominators.originalOrders)

/** Mean calendar days between the original order and the customer's next one. */
export const avgDaysToNext = (denominators: Denominators) =>
  denominators.timing
    ? safeShare(denominators.timing.sumDaysToNext, denominators.ordersWithNext)
    : null

/** Share of the orders with a next order whose next order came within `days`. */
export const gapShare = (denominators: Denominators, days: GapBucketDays) =>
  denominators.timing
    ? safeShare(
        denominators.timing.nextWithin[days],
        denominators.ordersWithNext,
      )
    : null

export type MedianGap =
  /** The median falls inside the buckets; `days` is interpolated. */
  | { kind: "value"; days: number }
  /** Fewer than half of the next orders arrived within the last bucket. */
  | { kind: "beyond"; days: GapBucketDays }
  /** No orders with a next order, or no timing columns. */
  | { kind: "none" }

/**
 * Median gap read off the cumulative buckets: the first bucket whose share
 * reaches 50%, linearly interpolated from the previous bucket's edge. An
 * approximation (the base row stores buckets, not every gap), good to a few
 * days at the volumes the report runs at.
 */
export function approxMedianDaysToNext(denominators: Denominators): MedianGap {
  const { timing, ordersWithNext } = denominators
  if (!timing || ordersWithNext <= 0) return { kind: "none" }
  let lowerDays = 0
  let lowerShare = 0
  for (const days of GAP_BUCKET_DAYS) {
    const share = timing.nextWithin[days] / ordersWithNext
    if (share >= 0.5) {
      const span = share - lowerShare
      const fraction = span > 0 ? (0.5 - lowerShare) / span : 0
      return { kind: "value", days: lowerDays + fraction * (days - lowerDays) }
    }
    lowerDays = days
    lowerShare = share
  }
  return { kind: "beyond", days: 365 }
}

export function buildRows(
  pairs: RawPair[],
  denominators: Denominators,
): SubsequentRow[] {
  return pairs.map(pair => ({
    ...pair,
    id: pair.itemKey,
    nextOrderShare: safeShare(
      pair.subsequentOrders,
      denominators.ordersWithNext,
    ),
    allOrdersShare: safeShare(
      pair.subsequentOrders,
      denominators.originalOrders,
    ),
    avgDaysToNext:
      pair.sumDaysToNext === null
        ? null
        : safeShare(pair.sumDaysToNext, pair.subsequentOrders),
  }))
}

const isNil = (value: unknown) => value === null || value === undefined

const compareValues = (a: unknown, b: unknown): number => {
  // Nulls (undefined shares) always sort last regardless of direction.
  if (isNil(a)) {
    return isNil(b) ? 0 : 1
  }
  if (isNil(b)) {
    return -1
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b
  }
  return String(a).localeCompare(String(b))
}

/**
 * Sorts by the first sort spec (nulls last), then breaks ties by next-order
 * count descending and title ascending so the order is stable across renders.
 */
export function sortRows(
  rows: SubsequentRow[],
  sorting: SortSpec[],
): SubsequentRow[] {
  const spec = sorting[0] ?? DEFAULT_SORTING[0]
  const key = spec.id as keyof SubsequentRow
  return [...rows].sort((a, b) => {
    const av = a[key]
    const bv = b[key]
    if (isNil(av) || isNil(bv)) {
      return compareValues(av, bv)
    }
    const primary = compareValues(av, bv) * (spec.desc ? -1 : 1)
    if (primary !== 0) {
      return primary
    }
    if (b.subsequentOrders !== a.subsequentOrders) {
      return b.subsequentOrders - a.subsequentOrders
    }
    return a.title.localeCompare(b.title)
  })
}
